import { FC, useEffect, useState, useContext } from 'react';
import { post } from '@/common/request';
import { isArray, urlParams } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { getFavoriteList, handleCollectOfFetch } from '@/common/api/siteselectionmap';
import { CollectListContext } from '../../ts-config';

import styles from './index.module.less';
import { Button, Checkbox, Input, Modal, Space } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import IconFont from '@/common/components/IconFont';
import V2Message from '@/common/components/Others/V2Hint/V2Message';

/**
 * @Description 选择文件夹弹窗
 */
const PickFolderModal: FC<{
  open,
  /** 选中加入收藏的 ids 数组 */
  selectedIds?: number[],
  setOpen,
  setCollectStatus?, // 收藏/取消收藏时更新状态
  callFn?, // 回调
}> = ({
  open,
  selectedIds = [],
  setOpen,
  setCollectStatus, // 收藏/取消收藏时更新状态
  callFn, // 回调
}) => {

  const requirementId = urlParams(location.search)?.requirementId; // 需求id

  const [isAddFolder, setIsAddFolder] = useState<any>(false);
  const [selected, setSelected] = useState<CheckboxValueType[]>([]);
  const [addFolderName, setAddFolderName] = useState('');
  const [folderList, setFolderList] = useState<any>([]);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [repeatTip, setRepeatTip] = useState('');
  const [showSpotFlag, setShowSpotFlag] = useState(false);

  const { setCollectList }: any = useContext(CollectListContext) || {};

  const btnStyle:any = { // 禁用按钮样式
    backgroundColor: '#c9e1fe',
    color: '#fff',
    borderRadius: '4px'
  };

  useEffect(() => {
    if (!open) return;
    console.log('selectedIds', selectedIds);
    methods.getFolderList();
  }, [open]);

  const methods = useMethods({
    /**
     * @description 获取文件夹列表
     */
    getFolderList: async () => {
      const params: any = {
        requirementId,
        locationId: Array.isArray(selectedIds) && selectedIds.length ? selectedIds[0] : null,
      };
      const { favoriteFolderListVOS: data, siteLocationFlag } = await getFavoriteList(params);
      setFolderList(isArray(data) ? data : []);
      setShowSpotFlag(siteLocationFlag);
      const targetSelected: Array<number> = [];
      data?.forEach((item: any) => {
        item.containFlag && (targetSelected.push(item.id));
      });
      setSelected(targetSelected);
    },

    /**
     * @description 选择文件夹复选框事件
     */
    checkboxChange: (checkedValues: CheckboxValueType[]) => {
      setSelected(checkedValues);
    },

    /**
     * @description 添加收藏夹
     */
    addFolder: async () => {
      if (!addFolderName || addFolderName.trim() === '') {
        V2Message.warning('请输入收藏夹名称');
        return;
      }
      try {
        // https://yapi.lanhanba.com/project/546/interface/api/70195
        await post('/clusterLocationFavor/saveFolder', { requirementId, name: addFolderName }, { needHint: false });
        V2Message.success('添加成功');
        methods.closeAddFolder();
        methods.getFolderList();
      } catch (err: any) {
        setRepeatTip(err.response?.data?.errMsg);
      }
    },
    // 取消添加收藏夹
    closeAddFolder: () => {
      setIsAddFolder(false);
      setAddFolderName('');
      setRepeatTip('');
    },

    onSubmit: () => {
      console.log('selectedIds', selectedIds);
      if (!Array.isArray(selectedIds) || !selectedIds.length) {
        V2Message.warning('请选择添加至少一条数据到收藏夹');
        return;
      }
      // if (!selected.length) {
      //   V2Message.warning('请选择收藏夹');
      //   return;
      // }
      const params: any = {
        requirementId,
        favourFolders: selected,
        locationId: selectedIds[0],
      };

      // 是否已收藏
      const collected = !!selected.length;

      // 批量收藏时，hasSelected一定为false
      const hasSelected = folderList?.find((item: any) => item.containFlag); // 是否之前有选中
      if (!hasSelected && !collected) {
        V2Message.warning('请选择收藏夹');
        return;
      }
      setConfirmLoading(true);
      // https://yapi.lanhanba.com/project/546/interface/api/70188
      handleCollectOfFetch(params).then(() => {
        let hintStr = '';
        if (hasSelected && !collected) { // 说明取消选中
          hintStr = '取消收藏成功';
        } else if (collected) {
          hintStr = selectedIds.length ? '批量收藏成功' : '操作成功';
        }
        hintStr && V2Message.success(hintStr);
        setCollectStatus && setCollectStatus(!!selected.length);
        // 批量收藏时
        setCollectList?.((state) => Array.from(new Set([...state, ...selectedIds])));
        methods.onCancel();
        callFn && callFn(collected); // 回调更新点位列表
      }).finally(() => setConfirmLoading(false));
    },

    onCancel: () => {
      setIsAddFolder(false);
      setSelected([]);
      setAddFolderName('');
      setOpen(false);
    },

  });

  return (
    <Modal
      bodyStyle={{ paddingBottom: 0 }}
      keyboard={false}
      title='选择收藏夹'
      open={open}
      onCancel={methods.onCancel}
      footer={
        <>
          <Button type='primary' loading={confirmLoading} disabled={isAddFolder} onClick={methods.onSubmit} style={isAddFolder ? btnStyle : {}}>确定</Button>
        </>
      }
    >
      <div className={styles.pickerFolderWrap}>
        <div className={styles.folderListContent}>
          <Checkbox.Group style={{ width: '100%' }} value={selected} onChange={methods.checkboxChange}>
            <Space direction='vertical'>
              { folderList.map((item, index) => {
                return <Checkbox key={index} value={item.id} >{item.name}{showSpotFlag && `（点位${item.siteLocationNum}）`}</Checkbox>;
              }) }
            </Space>
          </Checkbox.Group>
        </div>
        <div className={styles.folderFooter}>
          { !isAddFolder ? (
            <div className={styles.addBtn} onClick={() => setIsAddFolder(true)}>
              <IconFont iconHref='iconic_add_xiangqing' className='mr-4 fs-14' />
              添加收藏夹
            </div>) : (
            <div className={styles.addEnterContent}>
              { repeatTip && <div className={styles.errorTip}><ExclamationCircleOutlined /> {repeatTip}</div> }
              {/* <Input.Group compact className={styles.inputGroupContent}>

                <div className={styles.divider} />
                {/* <Button type='link' size='small' className={styles.cancelBtn} onClick={closeAddFolder}>取消</Button> */}
              {/* <Button type='link' size='small' className={styles.submitBtn} onClick={addFolder}>确定</Button> */}
              {/* </Input.Group> */}
              <div style={{ display: 'flex' }}>
                <Input
                  className={styles.folderInput}
                  value={addFolderName}
                  onChange={e => {
                    setAddFolderName(e.target.value);
                    setRepeatTip('');
                  }}
                  showCount
                  maxLength={10}
                />
                <div style={{ display: 'flex' }}>
                  <Button style={{ marginLeft: '10px', borderRadius: '4px' }} type='primary' className={styles.submitBtn} onClick={methods.addFolder}>新建</Button>
                  <Button style={{ marginLeft: '10px' }} type='link' onClick={methods.closeAddFolder}>取消</Button>
                </div>
              </div>
            </div>
          )
          }
        </div>
      </div>
    </Modal>
  );
};

export default PickFolderModal;
